/**
 * © 2024 Little Shilling, Inc. All rights reserved.
 * Shon Little
 * Created: 2024-01-17
 */

// Add third-party dependencies.
import { useTheme, Box } from '@mui/material';

/**
 * ReleveStudios component.
 * @example
 * return (
 *   <ReleveStudios />
 * )
 * @returns {React.ReactElement} component.
 */
const ReleveStudios = () => {
  // Add theme hooks.
  const theme = useTheme();

  // Set styles.
  const styles = {
    black: { fill: theme.palette.common.black },
    purple: { fill: theme.palette.primary.purple },
  };

  // Render component.
  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 379.59 257.46"
      aria-labelledby="title"
      aria-describedby="desc"
    >
      <Box component="title" id="title">
        Releve Studios logo
      </Box>
      <Box component="desc" id="desc">
        Logo for Releve Studios
      </Box>
      <Box
        component="path"
        sx={styles.black}
        d="M209,317.67a35.91,35.91,0,0,1,1.72-7q9.39-30,14.71-45.2t15.21-39.3a53.66,53.66,0,0,0-8.19,3.77c-2.68,1.51-4.78,2.8-6.28,3.84a11.23,11.23,0,0,1-2.88,1.67c-.73.11-1.73-.31-3-1.25s-1.89-1.86-1.89-2.76q17.1-9.48,29.43-11.39a47.83,47.83,0,0,1,11.48-.43A39.85,39.85,0,0,1,270,222a16.75,16.75,0,0,1,11.17,16.47,21.33,21.33,0,0,1-3.78,12.19,32.87,32.87,0,0,1-9,9.09,68.94,68.94,0,0,1-12.21,6.35,92.53,92.53,0,0,1-11.85,4.09q-4.89,1.24-9.4,2.06l4.27,6.06c2.53,3.6,4.07,5.76,4.64,6.49l4.28,5.57a50.71,50.71,0,0,0,5.49,6.35l5.5,5.38a66.06,66.06,0,0,0,7.21,6q3.66,2.61,8.06,5.23a7.5,7.5,0,0,1-2.87,4.29,10,10,0,0,1-4.58,2.23q-4.53.71-11.85-6.53A105.22,105.22,0,0,1,241.63,297a202.58,202.58,0,0,1-10.87-18l-12,44.24a10.81,10.81,0,0,1-6.78-1A5,5,0,0,1,209,317.67Zm24.55-48.86a84,84,0,0,0,17.9-6.19,42.83,42.83,0,0,0,13.86-10.57,21.65,21.65,0,0,0,5.74-14.44q0-8.67-6.23-11.68t-15.63-1.8Q242.36,238.88,233.56,268.81Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M270.13,295.15q0-11.07,10.31-24.41t21.31-15q9-1.39,9.05,6.66,0,8.79-9.6,17.12a52.25,52.25,0,0,1-21.42,11.37,14.46,14.46,0,0,0-.25,2.85,12.79,12.79,0,0,0,.19,2.29,12.62,12.62,0,0,0,1,2.84,5,5,0,0,0,2.95,2.6,10.45,10.45,0,0,0,5.34.33q7.44-1.14,15.2-8.39a57.62,57.62,0,0,0,12.52-17l2.44,1.57a53.86,53.86,0,0,1-14,19.79q-9.1,8.09-18.89,9.6-7.65,1.19-11.9-2.28T270.13,295.15Zm11-9q7.11-2,15-9.4t7.91-13.93c0-1.55-.69-2.21-2.08-2q-5.42.84-11.79,8.91A52.61,52.61,0,0,0,281.13,286.12Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M314.77,288.48q0-11.25,7.75-31.71a237.06,237.06,0,0,1,17.16-36.41q9.4-15.95,13.92-16.64,2.08-.33,3.55,2.69a15.85,15.85,0,0,1,1.59,6.29q0,5.64-4.2,16.61a144.56,144.56,0,0,1-12.11,23.83,104.13,104.13,0,0,1-16.56,21,69.39,69.39,0,0,0-1.33,11.34v.36q0,8.44,5.23,7.63c2.83-.44,5.86-2.8,9.06-7.08a75.55,75.55,0,0,0,8.57-15.09l2.92,1.14q-5.35,11.89-11.8,19t-13.37,8.21Q314.77,301.31,314.77,288.48Zm13.3-22.23a142,142,0,0,0,17.39-28.44q7.65-16.29,7.66-22.89-1.59.24-6.21,8.05a202.78,202.78,0,0,0-10.09,20.09A194.78,194.78,0,0,0,328.07,266.25Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M343.25,283.85q0-11.07,10.31-24.42t21.31-15q9-1.39,9.05,6.66,0,8.81-9.61,17.12a52.28,52.28,0,0,1-21.41,11.37,14.46,14.46,0,0,0-.25,2.85,13.93,13.93,0,0,0,.18,2.3,12.7,12.7,0,0,0,1,2.83,5,5,0,0,0,2.94,2.6,10.33,10.33,0,0,0,5.34.34q7.45-1.15,15.21-8.4a57.62,57.62,0,0,0,12.52-17l2.44,1.58a53.85,53.85,0,0,1-14,19.78q-9.09,8.09-18.88,9.6-7.65,1.19-11.9-2.27T343.25,283.85Zm11-9q7.11-2,15-9.41t7.92-13.93c0-1.54-.7-2.21-2.09-2q-5.4.84-11.78,8.91A52.66,52.66,0,0,0,354.25,274.82Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M388.85,276.31q0-3.42.5-14.62c.09-2.61.38-4.37.88-5.26s1.8-1.5,3.9-1.82c1.91-.3,3.08-.13,3.5.49s.62,2.21.62,4.73q0,2.44-.3,7.86t-.31,8.11a24.28,24.28,0,0,0,.43,5.67c.28,1,.87,1.46,1.76,1.32q3.89-.6,9.41-9.27a119,119,0,0,0,9.65-18.71q.25-7.86,1.89-11.17a6.23,6.23,0,0,1,4.67-3.77,3.8,3.8,0,0,1,3.81,1.12,5.37,5.37,0,0,1,1.24,3.47c0,.74-.61,2.54-1.84,5.42a26.22,26.22,0,0,0-1.83,10.29q0,8.55,5.25,7.74,4.89-.75,9.53-10.51l1.34.89q-5.13,13-12.94,14.22-7.59,1.17-10.61-9.72-12.78,25.43-22.26,26.89Q388.85,291,388.85,276.31Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M436.24,269.47q0-11.07,10.31-24.42t21.31-15q9-1.39,9.05,6.66,0,8.81-9.61,17.12a52.25,52.25,0,0,1-21.42,11.37,15.39,15.39,0,0,0-.24,2.85,13.93,13.93,0,0,0,.18,2.3,12.41,12.41,0,0,0,1,2.83,5,5,0,0,0,2.95,2.6,10.33,10.33,0,0,0,5.34.34q7.46-1.15,15.21-8.4a53.64,53.64,0,0,0,7-7.87c2-2.74,3.27-5.8,5.06-8.61.56-.86.93-1.94,1.86-2.49,2.43-1.43,2.16,1.91,1.66,3.18-5.41,13.63-18.45,27.42-33.45,29.73q-7.65,1.19-11.9-2.27T436.24,269.47Zm11-9q7.11-2,15-9.41t7.92-13.93c0-1.54-.7-2.21-2.09-2q-5.4.84-11.78,8.91A52.46,52.46,0,0,0,447.24,260.44Zm12.24-36.72c0-1.32,1.24-3.75,3.74-7.28s4.68-5.44,6.53-5.73,2.72.74,3,3q-8.44,9.47-10.83,12.91a3.21,3.21,0,0,1-1.77-1.27A3,3,0,0,1,459.48,223.72Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M206.87,387.67l1.41-2.76A16.62,16.62,0,0,0,219.45,389c6.34,0,9.1-2.66,9.1-6,0-9.29-20.85-3.58-20.85-16.11,0-5,3.87-9.29,12.48-9.29a19.1,19.1,0,0,1,10.5,3l-1.21,2.85a17.48,17.48,0,0,0-9.29-2.8c-6.19,0-9,2.76-9,6.14,0,9.29,20.85,3.68,20.85,16,0,5-4,9.24-12.63,9.24C214.37,392.07,209.39,390.28,206.87,387.67Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M257.57,361h-11.9v-3.1h27.39V361H261.15v30.77h-3.58Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M289.4,377.31v-19.4H293v19.26c0,8,3.77,11.71,10.35,11.71s10.4-3.68,10.4-11.71V357.91h3.49v19.4c0,9.73-5.23,14.76-13.89,14.76S289.4,387,289.4,377.31Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M339.62,357.91h13.79c10.84,0,18.14,7,18.14,16.93s-7.3,16.94-18.14,16.94H339.62Zm13.59,30.77c9,0,14.81-5.71,14.81-13.84S362.21,361,353.21,361h-10v27.67Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M391.19,357.91h3.58v33.87h-3.58Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M414.41,374.84c0-9.87,7.54-17.22,17.8-17.22S450,364.92,450,374.84s-7.6,17.23-17.76,17.23S414.41,384.71,414.41,374.84Zm32,0c0-8.08-6.05-14-14.18-14s-14.27,5.95-14.27,14,6.1,14,14.27,14A13.73,13.73,0,0,0,446.39,374.84Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M466.36,387.67l1.41-2.76A16.6,16.6,0,0,0,478.94,389c6.34,0,9.1-2.66,9.1-6,0-9.29-20.85-3.58-20.85-16.11,0-5,3.87-9.29,12.48-9.29a19.1,19.1,0,0,1,10.5,3L489,363.47a17.48,17.48,0,0,0-9.29-2.8c-6.19,0-8.95,2.76-8.95,6.14,0,9.29,20.85,3.68,20.85,16,0,5-4,9.24-12.63,9.24C473.86,392.07,468.88,390.28,466.36,387.67Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M207.67,429.25l.74-1.45a8.72,8.72,0,0,0,5.85,2.16c3.32,0,4.77-1.4,4.77-3.15,0-4.86-10.93-1.87-10.93-8.44,0-2.61,2-4.86,6.54-4.86a10,10,0,0,1,5.5,1.57l-.63,1.5a9.13,9.13,0,0,0-4.87-1.47c-3.24,0-4.69,1.44-4.69,3.21,0,4.87,10.93,1.93,10.93,8.39,0,2.61-2.08,4.84-6.62,4.84A9.62,9.62,0,0,1,207.67,429.25Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box component="path" sx={styles.black} d="M227,413.66h1.88V431.4H227Z" transform="translate(-206.87 -177.44)" />
      <Box
        component="path"
        sx={styles.black}
        d="M251.49,413.66V431.4h-1.55L238.54,417V431.4h-1.88V413.66h1.55l11.43,14.4v-14.4Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M271.78,422.53h1.8v6.77a9.8,9.8,0,0,1-6.51,2.25c-5.4,0-9.35-3.82-9.35-9s3.95-9,9.38-9a8.72,8.72,0,0,1,6.58,2.56l-1.16,1.19a7.49,7.49,0,1,0-5.37,12.62,7.65,7.65,0,0,0,4.63-1.37Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M288.78,422.48a1.91,1.91,0,0,1,3.81,0,1.91,1.91,0,1,1-3.81,0Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M308.24,413.66h7.23c5.68,0,9.5,3.65,9.5,8.87s-3.82,8.87-9.5,8.87h-7.23Zm7.13,16.12c4.71,0,7.75-3,7.75-7.25s-3-7.25-7.75-7.25h-5.25v14.5Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M342.08,426.66h-9.89l-2.13,4.74h-1.95l8.11-17.74h1.85l8.11,17.74h-2Zm-.69-1.52-4.26-9.53-4.25,9.53Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M366,413.66V431.4h-1.54L353,417V431.4h-1.87V413.66h1.54l11.43,14.4v-14.4Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M372.2,422.53a8.91,8.91,0,0,1,9.3-9,8.59,8.59,0,0,1,6.49,2.58l-1.19,1.19a7,7,0,0,0-5.25-2.1,7.35,7.35,0,1,0,0,14.7,6.94,6.94,0,0,0,5.25-2.13l1.19,1.19a8.59,8.59,0,0,1-6.51,2.61A8.9,8.9,0,0,1,372.2,422.53Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M406.57,429.78v1.62H394V413.66h12.16v1.62H395.9v6.31h9.17v1.6H395.9v6.59Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M420.88,422.48a1.9,1.9,0,1,1,3.8,0,1.9,1.9,0,1,1-3.8,0Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M451.47,426.66h-9.89l-2.13,4.74H437.5l8.11-17.74h1.85l8.11,17.74h-2Zm-.69-1.52-4.26-9.53-4.25,9.53Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M458.71,422.53a8.91,8.91,0,0,1,9.3-9,8.59,8.59,0,0,1,6.49,2.58l-1.19,1.19a7,7,0,0,0-5.25-2.1,7.35,7.35,0,1,0,0,14.7,6.94,6.94,0,0,0,5.25-2.13l1.19,1.19a8.59,8.59,0,0,1-6.51,2.61A8.9,8.9,0,0,1,458.71,422.53Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.black}
        d="M483.75,415.28h-6.24v-1.62h14.35v1.62h-6.24V431.4h-1.87Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.purple}
        d="M488,339.4c0,1.35-2,2.45-4.48,2.45h-270c-2.46,0-4.48-1.1-4.48-2.45s2-2.45,4.48-2.45h270C486,337,488,338.06,488,339.4Z"
        transform="translate(-206.87 -177.44)"
      />
      <Box
        component="path"
        sx={styles.purple}
        d="M486,179.27a.67.67,0,0,0,.1-.31c0-.17,0,0,0,0l0,.2.07.47.16,1,.36,2q.38,2,.81,3.93c.28,1.3.58,2.6.93,3.84a23,23,0,0,0,1.45,3.6c1.16,2.39,2.27,4.8,3.53,7.16l3.74,7.11c1.25,2.37,2.59,4.69,3.8,7.09s2.29,4.85,3.65,7.16c2.69,4.64,5.52,9.2,8.52,13.61,1.48,2.22,3,4.4,4.56,6.51l.58.81.14.2.22.33.44.68a16.57,16.57,0,0,1,1.17,2.59,26.42,26.42,0,0,1,1.19,4.73,104.63,104.63,0,0,1,.81,16.88c0,2.67,0,5.32.09,7.76,0,.61.08,1.2.14,1.75l.24,1.95.54,4c.38,2.63.79,5.23,1.26,7.87l.71,4,.35,2,.18,1,.09.51c0,.27.1.58.13,1a6.5,6.5,0,0,1-.53,3.29,5.41,5.41,0,0,1-2,2.35,5.69,5.69,0,0,1-2.61.86,7,7,0,0,1-2.83-.41,11,11,0,0,1-1.64-.75,12.73,12.73,0,0,1-2.61-1.92c-.62-.64-.65-.73-.92-1l-.65-.76c-1.76-2-3.51-4-5.29-6s-3.6-3.81-5.48-5.58c-.93-.89-1.89-1.75-2.86-2.59l-1.47-1.23c-.52-.42-1-.81-1.43-1.13a15.3,15.3,0,0,0-5.59-2.76,3.93,3.93,0,0,0-1.89,0,1.12,1.12,0,0,0-.49.34,2.13,2.13,0,0,0-.43.52c-.49.63-.57,2.46.09,4.46a17.14,17.14,0,0,0,1.28,3c.6,1.07,1.24,2.15,1.92,3.2a90,90,0,0,0,9.74,12c3.55,4,7.42,7.75,11,12a70.68,70.68,0,0,1,9.55,14,37.83,37.83,0,0,1,4.3,16.54,1.57,1.57,0,0,1-3.09.38v0c-2.46-10.16-9.06-18.31-16.37-25.77l-11.52-11.37A85.55,85.55,0,0,1,483.44,299c-.78-1.23-1.51-2.49-2.21-3.79a25.59,25.59,0,0,1-1.88-4.49,17.22,17.22,0,0,1-.83-5.23,11.34,11.34,0,0,1,1.64-6,9.76,9.76,0,0,1,2.21-2.53,10.39,10.39,0,0,1,3-1.67,12,12,0,0,1,6.13-.43,23.32,23.32,0,0,1,9,4c.67.47,1.25.92,1.8,1.35s1.12.89,1.68,1.33c1.09.91,2.18,1.83,3.23,2.78,2.11,1.89,4.11,3.87,6,5.87s3.8,4,5.63,6.05l1.13,1.24a3.81,3.81,0,0,0,.67.45l.22.1s-.14-.12-.85-.08a3.42,3.42,0,0,0-1.65.55,3.85,3.85,0,0,0-1.4,1.59,2.6,2.6,0,0,0-.29,1.29l-1.49-7.51c-.52-2.68-1-5.43-1.46-8.14l-.63-4.07-.31-2.07c-.11-.8-.18-1.56-.25-2.3-.25-3-.25-5.74-.23-8.48l.06-8a68.41,68.41,0,0,0-.25-7.55,21.76,21.76,0,0,0-.53-3.2,8.31,8.31,0,0,0-.38-1.16l-.09-.18,0-.1-.14-.23-.55-.88c-1.46-2.38-2.83-4.78-4.16-7.19q-4-7.26-7.61-14.59c-.62-1.22-1.26-2.43-1.91-3.63l-2-3.59c-1.26-2.43-2.32-5-3.39-7.49s-2.1-5.06-3-7.68-1.81-5.21-2.52-7.9l-1-4.15c-.29-1.39-.5-2.76-.7-4.12s-.35-2.72-.49-4.09l-.19-2.05-.07-1,0-.54v-.31a1.71,1.71,0,0,1,0-.26,2.14,2.14,0,0,1,.11-.56,1.32,1.32,0,0,1,2.42,1Zm68.29,45.13a7.13,7.13,0,0,0-1.32-.53c-6.76-2-16.44,5.92-19.11,10.71-4.58,8.22-5.36,17.22-2.37,19.87,2.81,2.49,7.46.33,9.7-1.66,1.64-1.46,3.48-5.17,2.36-7.37-1.57-3-4.72,5.38-7.21,1.64-3.61-5.45,6.3-16.44,10.94-18.37,5.61-2.35,5.42,7.92,3.85,13.92-.52,2-2,3.92-1,5.83.19.36.36.87.77.82a1,1,0,0,0,.58-.5C554.39,244.27,560.56,227.74,554.27,224.4Zm32.07,117a1.73,1.73,0,0,0-1-2.26l0,0c-4-1.61-6.42-6.24-9.21-10.48s-5.2-8.74-7.63-13.22c-4.81-9-9.38-18.2-13.82-27.45-2.26-4.62-4.44-9.25-6.64-13.93-.58-1.21-1-2.27-1.77-3.67l-.15-.28a6.37,6.37,0,0,0-.65-.93,4.4,4.4,0,0,0-1-.83,4.06,4.06,0,0,0-1.71-.52,4,4,0,0,0-1.29.11,4.06,4.06,0,0,0-.77.26,4.52,4.52,0,0,0-1.22.81,8.39,8.39,0,0,0-1.23,1.45c-.5.73-.86,1.36-1.22,2a58.73,58.73,0,0,0-3.4,7.61,51.79,51.79,0,0,0-2.29,8.09,27.78,27.78,0,0,0-.5,4.5,17.21,17.21,0,0,0,.11,2.47c.09.69.16,1.39.27,2.07.83,5.48,2.33,10.55,3.46,15.43a71.11,71.11,0,0,1,1.38,7.17,59,59,0,0,1,.42,7.18,97.27,97.27,0,0,1-1.4,14.76c-.79,5-1.86,10-2.77,15.15s-1.65,10.42-2.1,15.68a152.32,152.32,0,0,0,3.62,47.06c.57,2.56,1.26,5.09,2,7.61.38,1.25.73,2.51,1.13,3.76l.62,1.86.3.93.16.47a.53.53,0,0,0,.33.41,2.77,2.77,0,0,0,3.37-1l0-.05a.56.56,0,0,0,.06-.52l-.12-.46-.25-.93-.5-1.84c-.33-1.23-.59-2.48-.9-3.71-.56-2.48-1.09-5-1.51-7.48a151.18,151.18,0,0,1-1.9-15.1,158.64,158.64,0,0,1,.69-30.28c.59-5,1.43-9.95,2.48-14.95s2.17-10.06,3.12-15.26a95.57,95.57,0,0,0,1.72-16,65.85,65.85,0,0,0-.45-8.28,74.57,74.57,0,0,0-1.5-8c-1.24-5.17-2.69-10-3.42-14.77-.11-.59-.17-1.17-.25-1.76a9.18,9.18,0,0,1-.07-1.4,19.6,19.6,0,0,1,.34-3.19,43,43,0,0,1,1.9-6.8A50.81,50.81,0,0,1,544,276.3c.27-.49.55-1,.78-1.3.08-.14.27-.33,0-.06a2.86,2.86,0,0,1-.82.53,4.45,4.45,0,0,1-.66.23,3.89,3.89,0,0,1-3.72-1.15,2.38,2.38,0,0,1-.39-.51l.07.14c.48.89,1.08,2.18,1.63,3.3,2.23,4.64,4.48,9.33,6.8,14,4.69,9.27,9.65,18.43,15.25,27.25,2.78,4.42,5.67,8.79,8.89,13a77.26,77.26,0,0,0,5.11,6.17,15.69,15.69,0,0,0,7.39,4.64A1.74,1.74,0,0,0,586.34,341.43Z"
        transform="translate(-206.87 -177.44)"
      />
    </Box>
  );
};

// Export component.
export default ReleveStudios;
